import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, RefreshCw } from 'lucide-react';
import { Album } from '../../types';
import { albumsApi } from '../../api/albums';
import { Button } from '../UI/Button';
import { Modal } from '../UI/Modal';
import { track, EVENTS } from '../../lib/mixpanel';
import { getPortalUrl } from '../../lib/portal';
import styles from './InviteLinkPanel.module.css';

interface InviteLinkPanelProps {
  album: Album;
  onTokenRegenerated: (updated: Album) => void;
}

export function InviteLinkPanel({ album, onTokenRegenerated }: InviteLinkPanelProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [regenerating, setRegenerate] = useState(false);

  const inviteUrl = `${getPortalUrl('viewer')}/join/${album.inviteToken}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      toast.success('Link copied!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const handleRegenerate = async () => {
    setRegenerate(true);
    try {
      const updated = await albumsApi.regenerateToken(album._id);
      onTokenRegenerated(updated);
      setShowConfirm(false);
      track(EVENTS.ALBUM_TOKEN_REGENERATED, { album_id: album._id });
      toast.success('Invite link regenerated');
    } catch {
      toast.error('Failed to regenerate link');
    } finally {
      setRegenerate(false);
    }
  };

  return (
    <>
      <div className={styles['container']}>
        <label className={styles['label']}>
          Invite Link
        </label>
        <div className={styles['row']}>
          <div className={styles['url-display']}>
            {inviteUrl}
          </div>
          <button
            onClick={handleCopy}
            className={styles['copy-button']}
            title="Copy link"
            aria-label="Copy invite link"
          >
            <Copy className={styles['icon-sm']} />
          </button>
          <button
            onClick={() => setShowConfirm(true)}
            className={styles['regenerate-button']}
            title="Regenerate link"
            aria-label="Regenerate invite link"
          >
            <RefreshCw className={styles['icon-sm']} />
          </button>
        </div>
      </div>

      <Modal isOpen={showConfirm} onClose={() => setShowConfirm(false)} title="Regenerate link?" size="sm">
        <div className={styles['confirm-body']}>
          <p className={styles['confirm-text']}>
            The current link will stop working immediately. Anyone who hasn't joined yet will need the new link.
          </p>
          <div className={styles['confirm-actions']}>
            <Button variant="ghost" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button variant="danger" loading={regenerating} onClick={handleRegenerate}>Regenerate</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
